import type Database from '../../database/Database';
import type { FirstClassDocument } from '../../types';
import { Category } from '../../types';
import makeLogger from '../../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import audiobookController from '../audiobooks/audiobookController.platform';
// eslint-disable-next-line import/no-cycle
import { globalState } from '../models';
// eslint-disable-next-line import/no-cycle
import background, { portalGate as backgroundPortalGate } from '../portalGates/toBackground';

const logger = makeLogger(__filename);

export async function setupAudiobookContentLoading(database: Database) {
  if (!database.rxDbInstance) {
    logger.error('Database instance not available');
    return;
  }

  logger.info('Setting up audiobook content loading');
  await audiobookController.setup();
  logger.info('Audiobook controller setup complete');

  globalState.subscribe(
    (state) => state.persistent.bookwise.currentlyReadingId ?? state.persistent.currentlyReadingId,
    async (currentlyReadingId, prevCurrentlyReadingId) => {
      logger.info('currentlyReadingId changed', {
        currentlyReadingId,
        prevCurrentlyReadingId,
      });

      if (!currentlyReadingId) {
        const currentDocId = audiobookController.getSnapshot().docId;
        if (currentDocId) {
          logger.info('No doc selected, cleaning up controller');
          await audiobookController.stop();
        }
        return;
      }

      const doc = await database.collections.documents.findOne<FirstClassDocument>(currentlyReadingId);

      if (!doc || doc.category !== Category.Audiobook) {
        const currentDocId = audiobookController.getSnapshot().docId;
        if (currentDocId) {
          logger.info('Switching to non-audiobook, cleaning up controller');
          await audiobookController.stop();
        }
        return;
      }

      logger.info('Initializing audiobook controller', {
        docId: doc.id,
        prevCurrentlyReadingId,
      });

      const savedProgress = doc.source_specific_data?.audiobook?.position;
      const startingChunkId = savedProgress?.trackId;
      const startPosition = savedProgress?.positionInTrack;
      const savedPlaybackRate = doc.source_specific_data?.audiobook?.playbackRate;

      const shouldPlay = prevCurrentlyReadingId !== undefined &&
                         prevCurrentlyReadingId !== currentlyReadingId;

      try {
        await audiobookController.prepareDocumentForPlayback(doc.id, {
          chunkId: startingChunkId,
          startPositionInChunk: startPosition,
          shouldPlay,
          playbackRate: savedPlaybackRate,
        });
        logger.info('Audiobook controller initialized successfully', {
          docId: doc.id,
          shouldPlay,
        });
      } catch (error) {
        logger.error('Failed to initialize audiobook controller', {
          docId: doc.id,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    },
    {
      fireImmediately: true,
    },
  );

  const zustandState = globalState.getState();
  if (!zustandState.persistentStateLoaded) {
    backgroundPortalGate.on('persistentStateLoadedFromServer', async () => {
      logger.info('persistentStateLoadedFromServer event received');
    });
  }


  background.loadAudiobookTracksInBackground();
}
